import { Component, OnInit, ChangeDetectionStrategy, Input } from '@angular/core';
import { Router } from '@angular/router';
import { enumOrganizationRole } from '@app/data-layer/enums/organizations';
import { CompanyModel } from '@app/data-layer/models/companies';
import { OrganizationMemberModel } from '@app/data-layer/models/organizations/organization-member-model';
import { OrganizationModel } from '@app/data-layer/models/organizations/organization-model';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ACCESS_TOKEN } from '@constants/setting-keys.const';
import { CompaniesService } from '@services/companies.service';
import { GenericService } from '@services/generic.service';
import { Observable, ReplaySubject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'organization-companies-attached-card',
  templateUrl: './organization-companies-attached-card.component.html',
  styleUrls: ['./organization-companies-attached-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationCompaniesAttachedCardComponent implements OnInit {
  @Input() public organization: OrganizationModel;
  @Input() public organizationMembers: OrganizationMemberModel[];

  public companies: CompanyModel[];
  public isTablet$: Observable<boolean>;
  public enumOrganizationMemberRole: typeof enumOrganizationRole = enumOrganizationRole;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private router: Router,
    private genericService: GenericService,
    private companiesService: CompaniesService,
    private jwtHelperService: JwtHelperService,
  ) {}

  ngOnInit() {
    this.isTablet$ = this.genericService.isTablet$;
    this.companies = this.organization.companies.map((company: CompanyModel) => ({ ...company, color: this.getRandomColor }));
    this.sortAlphabeticallyByBusinessName();
  }

  public redirectToCreateCompany(isFirstCompany: boolean = false): void {
    if (isFirstCompany) {
      const company = this.companiesService.getCompany();
      this.companiesService
        .getCompanyByIdFromServer(company.id)
        .pipe(take(1))
        .subscribe((company) => {
          this.router.navigate(['companies', 'account-settings', 'create'], {
            queryParams: { isFirstCompany: company.id },
          });
        });
    } else {
      this.router.navigate(['companies', 'account-settings', 'create']);
    }
  }

  public redirectToCompanyView(companyId: number): void {
    this.companiesService
      .getCompanyAsync(true, true, companyId)
      .pipe(
        switchMap((company) => this.loadUserBetaFlags(company)),
        takeUntil(this.destroy$),
      )
      .subscribe((betaFlags) => {
        const selectedCompany = this.companies.find((company) => company.id === companyId);
        this.companiesService.setDefaultCompany(selectedCompany);
        this.companiesService.setUserBetaFlags(betaFlags);
        this.router.navigate(['/companies', 'account-settings', 'company-view', companyId]);
      });
  }

  public get isCompaniesExist(): boolean {
    return this.companies?.length > 1 || (this.companies?.length > 0 && !!this.companies[0].businessName);
  }

  public get getRandomColor(): string {
    return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
  }

  public organizationCompanyMembers(companyId: number): OrganizationMemberModel[] {
    return this.organizationMembers
      .filter((organizationMember) => organizationMember.company.id === companyId)
      .filter((member, index, self) => {
        return index === self.findIndex((m) => m.user.userId === member.user.userId);
      });
  }

  private loadUserBetaFlags(company?: CompanyModel): Observable<UserBetaFlagModel[]> {
    return this.companiesService.getUserBetaFlags({
      userExternalId: this.userExternalId,
      companyId: company.id,
      organizationId: company.organizationId,
    });
  }

  private get userExternalId(): string {
    return this.jwtHelperService.decodeToken(localStorage.getItem(ACCESS_TOKEN))?.user_external_id || null;
  }

  private sortAlphabeticallyByBusinessName() {
    this.companies.sort((a, b) => {
      if (a.businessName < b.businessName) {
        return -1;
      }
      if (a.businessName > b.businessName) {
        return 1;
      }
      return 0;
    });
  }
}
